import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppDispatch, AppThunk } from "@/app/store";
import {
  MPDashboardZipCode,
  MPDashboardSummary,
  LastSyncInfo,
} from "../api/getMPDashboard/types";
import { getMPDashboard } from "../api/getMPDashboard/getMPDashboardApi";
import { refreshMPDashboard } from "../api/refreshMPDashboard/refreshMPDashboardApi";

interface MPDashboardState {
  dashboardData: {
    summary: MPDashboardSummary;
    items: MPDashboardZipCode[];
    lastSync: LastSyncInfo | null;
  };
  loading: boolean;
  refreshing: boolean;
  error: string | null;
}

const initialState: MPDashboardState = {
  dashboardData: {
    summary: {
      totalSales: 0,
      totalCustomers: 0,
      qualifiedZipCount: 0,
    },
    items: [],
    lastSync: null,
  },
  loading: false,
  refreshing: false,
  error: null,
};

const mpDashboardSlice = createSlice({
  name: "mpDashboard",
  initialState,
  reducers: {
    setDashboardData: (
      state,
      action: PayloadAction<MPDashboardState["dashboardData"]>,
    ) => {
      state.dashboardData = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setRefreshing: (state, action: PayloadAction<boolean>) => {
      state.refreshing = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
  },
});

export const { setDashboardData, setLoading, setRefreshing, setError } =
  mpDashboardSlice.actions;

export const fetchMPDashboardDataAction =
  (intacctId: string): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(setLoading(true));
    dispatch(setError(null));
    try {
      const response = await getMPDashboard(intacctId);
      dispatch(
        setDashboardData({
          summary: response.data.summary,
          items: response.data.items,
          lastSync: response.data.lastSync || null,
        }),
      );
    } catch (error) {
      if (error instanceof Error) {
        dispatch(setError(error.message));
      } else {
        dispatch(setError("Failed to fetch market presence dashboard data."));
      }
    } finally {
      dispatch(setLoading(false));
    }
  };

export const refreshMPDashboardDataAction =
  (intacctId: string): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(setRefreshing(true));
    dispatch(setError(null));
    try {
      await refreshMPDashboard();
      const response = await getMPDashboard(intacctId);
      dispatch(
        setDashboardData({
          summary: response.data.summary,
          items: response.data.items,
          lastSync: response.data.lastSync || null,
        }),
      );
    } catch (error) {
      if (error instanceof Error) {
        dispatch(setError(error.message));
      } else {
        dispatch(setError("Failed to refresh market presence dashboard data."));
      }
      throw error;
    } finally {
      dispatch(setRefreshing(false));
    }
  };

export default mpDashboardSlice.reducer;
